import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { useForm, UseFormReturnType } from '@mantine/form';
import { useAuth } from 'context/auth-context';
import useSalesPortalAgreementPreview from 'hooks/salesPortal/useSalesPortalAgreementPreview';
import {
  Address,
  BillingPlanRetrieve,
  DiscountRetrieve,
  SalesPortalAgreementPreview,
  SalesPortalCheckoutProgressWrite,
  SalesPortalCheckoutResponse,
  SalesPortalPackageRetrieve,
} from 'types';
import { emailValidator, nameValidator, requiredPhoneValidator } from 'utils/form-validators';
import { formatPhone } from 'utils/formatters';

import { CheckoutPhase, CheckoutStep, CHECKOUT_STEP_ORDER } from './checkoutFlowStates';
import {
  buildCheckoutStorageKey,
  CheckoutResumeSource,
  clearAllPersistedCheckoutFields,
  clearPersistedCheckoutFields,
  deriveRestoredStepState,
  persistCheckoutFields,
  readPersistedCheckoutFields,
  toAthleteInformationFormValues,
} from './checkoutSessionStorage';
import useCheckoutProgressSync from './useCheckoutProgressSync';
import { calculateLoggedInAthleteEmail, calculateLoggedInAthleteFullName } from './utils';

export type WhoIsThisFormOption = 'myself' | `subAccount` | 'newLinkedAccount' | 'newDependentAccount';

export interface CheckoutFormValues {
  // Step: Who Is This For
  whoIsThisFor: WhoIsThisFormOption;
  profileId: string | undefined;

  // Step: Athlete Information
  athleteInformation: {
    athleteId: string;
    email: string;
    firstName: string;
    lastName: string;
    phone?: string | null;
    dob?: Date | null;
    gender?:
      | 'Male'
      | 'Female'
      | 'Transgender'
      | 'Non-binary/Non-conforming'
      | 'Prefer not to respond'
      | ''
      | null;
    address?: Address | null;
    medicalNotes?: string | null;
    emergencyName?: string | null;
    emergencyPhone?: string | null;
    surname: string;
  };

  // Step: Agreement & Waiver
  agreementWaiver: {
    acknowledged: boolean;
    // One typed-initials value per marker occurrence index in the rendered agreement.
    initials: string[];
    // Drawn signature (data URI) for every document signed on the step.
    signature: string | null;
  };

  // Step: Payment
  paymentMethod: {
    selectedPaymentMethodId: string | null;
    stripeConfirmationToken: string | null;
  };
}

export const INITIAL_AGREEMENT_WAIVER_VALUES: CheckoutFormValues['agreementWaiver'] = {
  acknowledged: false,
  initials: [],
  signature: null,
};

// The Agreement & Waiver step's derived state, shared between the step component
// and the step machine (skip + completion rules).
export interface AgreementWaiverState {
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  preview: SalesPortalAgreementPreview | undefined;
  // The buyer must sign a waiver now: the gym requires one, it isn't on file for the
  // target athlete, and the target isn't a linked profile (they sign at activation).
  waiverNeeded: boolean;
  waiverDeferred: boolean;
  /** A signed waiver is on file for the athlete the purchase is for. */
  waiverOnFile: boolean;
}

function targetsExistingAthlete(whoIsThisFor: WhoIsThisFormOption): boolean {
  return whoIsThisFor === 'myself' || whoIsThisFor === 'subAccount';
}

/**
 * The waiver requirement for the athlete the purchase is for. The preview's
 * `waiverOnFile` and `waiverDeferred` describe an existing athlete — the selected
 * sub-account, else the signed-in buyer's own — so they apply only to `myself` and
 * `subAccount`. A new linked profile signs its own waiver at activation; a new
 * dependent has nothing on file and is signed for by the buyer. Anything short of a
 * confirmed on-file waiver (including the anonymous `null`) keeps the requirement on.
 */
export function resolveWaiverRequirement(
  preview: SalesPortalAgreementPreview | undefined,
  whoIsThisFor: WhoIsThisFormOption,
): Pick<AgreementWaiverState, 'waiverNeeded' | 'waiverDeferred' | 'waiverOnFile'> {
  const existingAthlete = targetsExistingAthlete(whoIsThisFor);
  const waiverDeferred =
    whoIsThisFor === 'newLinkedAccount' || (existingAthlete && preview?.waiverDeferred === true);
  const waiverOnFile = existingAthlete && preview?.waiverOnFile === true;
  const waiverNeeded = !!preview?.waiverRequired && !waiverOnFile && !waiverDeferred;
  return { waiverNeeded, waiverDeferred, waiverOnFile };
}

export function hasDocumentsToSign(
  agreement: Pick<AgreementWaiverState, 'preview' | 'waiverNeeded'>,
): boolean {
  return !!agreement.preview?.hasContract || agreement.waiverNeeded;
}

export function isAgreementWaiverComplete(
  agreement: Pick<AgreementWaiverState, 'preview' | 'waiverNeeded'>,
  values: CheckoutFormValues['agreementWaiver'],
): boolean {
  const { preview } = agreement;
  if (!preview) return false;

  const initialsComplete =
    !preview.hasContract ||
    (values.acknowledged &&
      Array.from({ length: preview.markerCount }).every(
        (_, index) => (values.initials[index] ?? '').trim().length > 0,
      ));
  const signatureComplete = !hasDocumentsToSign(agreement) || !!values.signature;
  return initialsComplete && signatureComplete;
}

/**
 * Steps whose values the Who Is This For step resets when the purchase target changes,
 * so the buyer confirms them again for the new target.
 */
const PURCHASE_TARGET_STEPS: CheckoutStep[] = ['athleteInformation', 'agreementWaiver'];

// Form paths validated when completing the Athlete Information step. Validating per
// step (instead of whole-form validate()) is what lets later steps carry their own
// rules without blocking earlier ones.
const ATHLETE_INFORMATION_FIELDS: Array<keyof CheckoutFormValues['athleteInformation']> = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'dob',
  'gender',
  'address',
  'emergencyName',
  'emergencyPhone',
  'medicalNotes',
];

export interface CompleteStepOptions {
  // Athlete-information fields shown read-only as "on file" for a returning buyer (spec
  // Item 9). They render no input that could normalize or correct the stored value, so they
  // are excluded from step validation — the same reasoning as the hidden emergency phone.
  lockedFields?: ReadonlySet<keyof CheckoutFormValues['athleteInformation']>;
}

interface CheckoutContextType {
  form: UseFormReturnType<CheckoutFormValues>;
  packageData: SalesPortalPackageRetrieve;
  billingPlan: BillingPlanRetrieve;
  // Athlete Information
  athleteInfoWasPrepopulated: boolean;
  setAthleteInfoWasPrepopulated: (wasPrepopulated: boolean) => void;
  // Accordion step machine
  steps: CheckoutStep[];
  openStep: CheckoutStep | null;
  setOpenStep: (step: CheckoutStep | null) => void;
  isStepCompleted: (step: CheckoutStep) => boolean;
  isStepSkipped: (step: CheckoutStep) => boolean;
  isStepUnlocked: (step: CheckoutStep) => boolean;
  isStepInvalidated: (step: CheckoutStep) => boolean;
  /**
   * Whether a completed step offers an Edit action. The account step never does: it
   * completes from the session and opens read-only; a different buyer logs out instead.
   */
  isStepEditable: (step: CheckoutStep) => boolean;
  completeStep: (step: CheckoutStep, options?: CompleteStepOptions) => boolean;
  /** Opens a step that is not locked: any completed step, or the current one. */
  expandStep: (step: CheckoutStep) => void;
  /**
   * Logs the buyer out without leaving the page. Once the session is gone, drops the saved
   * progress of every package in the tab and asks the caller to restart the checkout.
   */
  signOut: () => Promise<void>;
  isSigningOut: boolean;
  reopenStepForError: (step: CheckoutStep, message?: string) => void;
  allStepsCompleted: boolean;
  // A charge-time payment failure (declined / expired saved card), shown inline on the
  // Payment step until it is re-confirmed (spec Item 9).
  paymentStepError: string | null;
  // Agreement & Waiver
  agreement: AgreementWaiverState;
  // Post-checkout phases
  phase: CheckoutPhase;
  setPhase: (phase: CheckoutPhase) => void;
  // Same-session resume (spec Item 2) or recovery-email resume (spec Item 11)
  wasResumedFromStorage: boolean;
  resumeSource: CheckoutResumeSource;
  // Order summary
  appliedDiscount: DiscountRetrieve | null;
  setAppliedDiscount: (discount: DiscountRetrieve | null) => void;
  loggedInAthleteFullName: string;
  loggedInAthleteEmail: string;
  // The enriched checkout response the confirmation picks its variant from (spec Item 1).
  checkoutResult: SalesPortalCheckoutResponse | null;
  setCheckoutResult: (result: SalesPortalCheckoutResponse) => void;
  kiloClassErrorMessage: string | null;
  setKiloClassErrorMessage: (message: string | null) => void;
}

export const INITIAL_ATHLETE_INFORMATION_VALUES: CheckoutFormValues['athleteInformation'] = {
  athleteId: '',
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  dob: null,
  gender: null,
  address: null,
  medicalNotes: null,
  emergencyName: null,
  emergencyPhone: null,
  surname: '',
};

const CheckoutContext = createContext<CheckoutContextType | null>(null);

export const CheckoutProvider = ({
  children,
  packageData,
  billingPlan,
  onStepCompleted,
  onProgressSave,
  onSignedOut,
}: {
  children: ReactNode;
  packageData: SalesPortalPackageRetrieve;
  billingPlan: BillingPlanRetrieve;
  // Fired after a step is successfully completed. Kept as a prop so the provider
  // stays renderable without a MixpanelProvider (unit tests, Storybook).
  onStepCompleted?: (step: CheckoutStep) => void;
  // Fired (debounced) with the buyer's non-payment progress for the server-side save
  // (spec Item 11). Same reasoning: injected so the provider needs no QueryClient.
  onProgressSave?: (payload: SalesPortalCheckoutProgressWrite) => void;
  /** Fired after `signOut` succeeds, so the caller can restart the checkout. */
  onSignedOut?: () => void;
}): JSX.Element => {
  // Same-session resume (spec Item 2): a snapshot of the non-payment fields keyed
  // by gym+package+plan. Read once at mount — a restored snapshot both prefills the
  // form and suppresses the server prepopulate so the buyer's edits win.
  const [storageKey] = useState<string>(() =>
    buildCheckoutStorageKey(packageData.gym.gymId, packageData.packageId, billingPlan.billingPlanId),
  );
  const [restoredSnapshot] = useState(() => readPersistedCheckoutFields(storageKey));
  // A snapshot also remembers the step the buyer left on (spec Item 11): reopen there
  // with the earlier steps already summarized, never past Agreement & Waiver.
  const [restoredStepState] = useState(() => deriveRestoredStepState(restoredSnapshot?.currentStep));
  // The recovery landing page tags the snapshot it writes as `email` (spec Item 11).
  const resumeSource: CheckoutResumeSource = restoredSnapshot?.source ?? 'session';

  const [athleteInfoWasPrepopulated, setAthleteInfoWasPrepopulated] = useState<boolean>(
    () => !!restoredSnapshot,
  );
  const [checkoutResult, setCheckoutResult] = useState<SalesPortalCheckoutResponse | null>(null);
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountRetrieve | null>(null);
  const [phase, setPhase] = useState<CheckoutPhase>('checkout');
  const [kiloClassErrorMessage, setKiloClassErrorMessage] = useState<string | null>(null);
  const [paymentStepError, setPaymentStepError] = useState<string | null>(null);

  const { session, logoutInPlace } = useAuth();

  const loggedInAthleteFullName = calculateLoggedInAthleteFullName(session);
  const loggedInAthleteEmail = calculateLoggedInAthleteEmail(session);
  const pkgConfig = packageData.packagesalesportalconfiguration;

  const form = useForm<CheckoutFormValues>({
    initialValues: {
      whoIsThisFor: restoredSnapshot?.whoIsThisFor ?? 'myself',
      profileId: restoredSnapshot?.profileId,
      athleteInformation: restoredSnapshot
        ? toAthleteInformationFormValues(restoredSnapshot)
        : {
            ...INITIAL_ATHLETE_INFORMATION_VALUES,
            email: session?.user.email ?? '',
          },
      // Attestations and payment fields are never restored: the agreement is
      // re-accepted per purchase (spec Item 5) and payment data never hits storage.
      agreementWaiver: { ...INITIAL_AGREEMENT_WAIVER_VALUES },
      paymentMethod: {
        selectedPaymentMethodId: null,
        stripeConfirmationToken: null,
      },
    },
    validate: {
      athleteInformation: {
        firstName: value => nameValidator(value),
        lastName: value => nameValidator(value),
        email: value => emailValidator(value),
        phone: value => requiredPhoneValidator(value ?? ''),
        gender: value => (pkgConfig.genderRequired && !value ? 'Gender is required' : null),
        dob: value => (pkgConfig.dobRequired && !value ? 'Date of Birth is required' : null),
        address: value => (pkgConfig.addressRequired && !value ? 'Address is required' : null),
        emergencyName: value =>
          pkgConfig.emergencyNameRequired && !value
            ? 'Emergency Contact name is required'
            : value && value.length > 128
            ? 'Emergency name must be between 1-128 characters'
            : null,
        // SUPP-1359: only format-check emergency phone when shown. A hidden field has no
        // PhoneInput to normalize its prepopulated digits-only value, which would else fail.
        emergencyPhone: value =>
          !pkgConfig.emergencyPhoneVisible
            ? null
            : pkgConfig.emergencyPhoneRequired && !value
            ? 'Emergency Phone Number is required'
            : value
            ? requiredPhoneValidator(value)
            : null,
        medicalNotes: value => (pkgConfig.notesRequired && !value ? 'Medical Notes is required' : null),
      },
    },
    transformValues: values => ({
      ...values,
      athleteInformation: {
        ...values.athleteInformation,
        phone: formatPhone(values.athleteInformation.phone),
        emergencyPhone: formatPhone(values.athleteInformation.emergencyPhone),
      },
    }),
  });

  // Waivers and their on-file/deferral state resolve server-side for the target athlete:
  // the selected sub-account profile, else the signed-in buyer's own athlete, else none
  // when the purchase creates a new one. Signing in and changing the plan or target
  // refetch via the query key.
  const previewProfileId = form.values.whoIsThisFor === 'subAccount' ? form.values.profileId : undefined;
  const {
    data: agreementPreviewData,
    isLoading: agreementPreviewLoading,
    isError: agreementPreviewError,
    refetch: refetchAgreementPreview,
  } = useSalesPortalAgreementPreview({
    gym: packageData.gym?.gymId,
    packageId: packageData.packageId,
    billingPlanId: billingPlan.billingPlanId,
    profileId: previewProfileId,
    forNewAthlete: !targetsExistingAthlete(form.values.whoIsThisFor),
    sessionUserId: session?.user?.userId,
  });
  const agreementPreview = agreementPreviewData?.data;

  const { waiverNeeded, waiverDeferred, waiverOnFile } = resolveWaiverRequirement(
    agreementPreview,
    form.values.whoIsThisFor,
  );
  const agreementWaiverSatisfied = isAgreementWaiverComplete(
    { preview: agreementPreview, waiverNeeded },
    form.values.agreementWaiver,
  );

  const agreement: AgreementWaiverState = {
    isLoading: agreementPreviewLoading,
    isError: agreementPreviewError,
    refetch: refetchAgreementPreview,
    preview: agreementPreview,
    waiverNeeded,
    waiverDeferred,
    waiverOnFile,
  };

  const isStepSkipped = useCallback(
    (step: CheckoutStep): boolean => {
      if (step === 'agreementWaiver') {
        // Only a loaded preview may skip the step — while loading (or on error) the
        // step stays visible and blocks completion rather than silently waiving it.
        if (!agreementPreview) return false;
        return !hasDocumentsToSign({ preview: agreementPreview, waiverNeeded });
      }
      if (step === 'payment') return !billingPlan.salesPortalRequiresPaymentMethod;
      return false;
    },
    [billingPlan.salesPortalRequiresPaymentMethod, agreementPreview, waiverNeeded],
  );

  // The billing plan is pre-selected by the route the buyer lands on, so the step
  // starts completed; editing it re-opens the inline plan list.
  const [manuallyCompletedSteps, setManuallyCompletedSteps] = useState<Set<CheckoutStep>>(
    () => new Set<CheckoutStep>(restoredStepState?.completedSteps ?? ['billingPlan']),
  );
  const [invalidatedSteps, setInvalidatedSteps] = useState<Set<CheckoutStep>>(() => new Set());

  /**
   * Whether a step in `completed` still counts as confirmed. Agreement & Waiver counts
   * only while the entered agreement satisfies the current requirement: changing the
   * purchase target resets the agreement and can raise the waiver requirement.
   */
  const isConfirmedIn = useCallback(
    (step: CheckoutStep, completed: Set<CheckoutStep>): boolean =>
      completed.has(step) && (step !== 'agreementWaiver' || agreementWaiverSatisfied),
    [agreementWaiverSatisfied],
  );

  const isStepCompleted = useCallback(
    (step: CheckoutStep): boolean => {
      if (isStepSkipped(step)) return true;
      if (step === 'account') return !!session?.user;
      return isConfirmedIn(step, manuallyCompletedSteps);
    },
    [isStepSkipped, session, isConfirmedIn, manuallyCompletedSteps],
  );

  const steps = CHECKOUT_STEP_ORDER.filter(step => !isStepSkipped(step));

  const firstIncompleteStep = useCallback((): CheckoutStep | null => {
    return CHECKOUT_STEP_ORDER.find(step => !isStepSkipped(step) && !isStepCompleted(step)) ?? null;
  }, [isStepSkipped, isStepCompleted]);

  const [openStep, setOpenStep] = useState<CheckoutStep | null>(() => {
    if (!session?.user) return 'account';
    return restoredStepState?.openStep ?? 'whoIsThisFor';
  });

  const allStepsCompleted = steps.every(step => isStepCompleted(step));

  const isStepUnlocked = useCallback(
    (step: CheckoutStep): boolean => {
      if (isStepCompleted(step)) return true;
      const stepIndex = CHECKOUT_STEP_ORDER.indexOf(step);
      return CHECKOUT_STEP_ORDER.slice(0, stepIndex)
        .filter(previous => !isStepSkipped(previous))
        .every(previous => isStepCompleted(previous));
    },
    [isStepCompleted, isStepSkipped],
  );

  const isStepInvalidated = useCallback(
    (step: CheckoutStep): boolean => invalidatedSteps.has(step),
    [invalidatedSteps],
  );

  const validateStep = useCallback(
    (step: CheckoutStep, options?: CompleteStepOptions): boolean => {
      if (step === 'athleteInformation') {
        const results = ATHLETE_INFORMATION_FIELDS.filter(field => !options?.lockedFields?.has(field)).map(
          field => form.validateField(`athleteInformation.${field}`).hasError,
        );
        return !results.some(Boolean);
      }
      if (step === 'payment') {
        return (
          !billingPlan.salesPortalRequiresPaymentMethod ||
          !!form.values.paymentMethod.selectedPaymentMethodId ||
          !!form.values.paymentMethod.stripeConfirmationToken
        );
      }
      if (step === 'agreementWaiver') return agreementWaiverSatisfied;
      return true;
    },
    [form, billingPlan.salesPortalRequiresPaymentMethod, agreementWaiverSatisfied],
  );

  const advanceFromStep = useCallback(
    (step: CheckoutStep, completed: Set<CheckoutStep>): void => {
      const next =
        CHECKOUT_STEP_ORDER.find(
          candidate =>
            !isStepSkipped(candidate) &&
            !(candidate === 'account' ? !!session?.user : isConfirmedIn(candidate, completed)),
        ) ?? null;
      setOpenStep(next === step ? step : next);
    },
    [isStepSkipped, session, isConfirmedIn],
  );

  const completeStep = useCallback(
    (step: CheckoutStep, options?: CompleteStepOptions): boolean => {
      if (!validateStep(step, options)) return false;

      const nextCompleted = new Set([...manuallyCompletedSteps, step]);
      setManuallyCompletedSteps(nextCompleted);
      setInvalidatedSteps(previous => {
        if (!previous.has(step)) return previous;
        return new Set([...previous].filter(invalidated => invalidated !== step));
      });
      if (step === 'payment') setPaymentStepError(null);
      advanceFromStep(step, nextCompleted);
      onStepCompleted?.(step);
      return true;
    },
    [validateStep, manuallyCompletedSteps, advanceFromStep, onStepCompleted],
  );

  const isStepEditable = useCallback(
    (step: CheckoutStep): boolean => step !== 'account' && isStepCompleted(step),
    [isStepCompleted],
  );

  const expandStep = useCallback(
    (step: CheckoutStep): void => {
      if (!isStepUnlocked(step)) return;
      setOpenStep(step);
    },
    [isStepUnlocked],
  );

  // Server-side step errors (e.g. duplicate email) both re-open and un-complete the
  // step, so Confirm purchase stays blocked until it is re-confirmed. A payment-step
  // error also keeps its message inline so the buyer can pick another method in place.
  const reopenStepForError = useCallback((step: CheckoutStep, message?: string): void => {
    setManuallyCompletedSteps(previous => new Set([...previous].filter(completed => completed !== step)));
    setOpenStep(step);
    if (step === 'payment') setPaymentStepError(message ?? null);
  }, []);

  // Account completion is session-derived: when a buyer signs in or creates an
  // account inside the Account step, advance past it automatically.
  const sessionUserId = session?.user?.userId ?? null;
  const previousSessionUserIdRef = useRef(sessionUserId);
  useEffect(() => {
    const signedInJustNow = !previousSessionUserIdRef.current && !!sessionUserId;
    previousSessionUserIdRef.current = sessionUserId;
    if (signedInJustNow && openStep === 'account') {
      setOpenStep(firstIncompleteStep());
    }
  }, [sessionUserId, openStep, firstIncompleteStep]);

  const [isSigningOut, setIsSigningOut] = useState(false);
  const signOut = useCallback(async (): Promise<void> => {
    setIsSigningOut(true);
    const signedOut = await logoutInPlace();
    if (!signedOut) {
      setIsSigningOut(false);
      return;
    }
    clearAllPersistedCheckoutFields();
    onSignedOut?.();
  }, [logoutInPlace, onSignedOut]);

  // A restored open step may name a panel this package skips (no agreement to
  // accept): once the preview settles, fall through to the first incomplete step. A
  // session that arrives after mount is handled by the account effect above, whose
  // firstIncompleteStep() lands on the same restored target.
  useEffect(() => {
    if (!openStep || openStep === 'account' || agreementPreviewLoading) return;
    if (isStepSkipped(openStep)) {
      setOpenStep(firstIncompleteStep());
    }
  }, [openStep, agreementPreviewLoading, isStepSkipped, firstIncompleteStep]);

  // Same-session persistence (spec Item 2): snapshot the allowlisted non-payment
  // fields plus the open step once the buyer has entered anything or completed a
  // step. Cleared when the purchase completes.
  const formValues = form.values;
  const formIsDirty = form.isDirty();
  const hasProgressed = [...manuallyCompletedSteps].some(step => step !== 'billingPlan');
  const isViewingSignedInAccount = !!sessionUserId && openStep === 'account';
  useEffect(() => {
    if (phase !== 'checkout' || isSigningOut || isViewingSignedInAccount) return;
    if (!formIsDirty && !hasProgressed) return;
    persistCheckoutFields(storageKey, formValues, openStep);
  }, [
    phase,
    isSigningOut,
    isViewingSignedInAccount,
    formIsDirty,
    hasProgressed,
    formValues,
    openStep,
    storageKey,
  ]);

  // Server-side progress (spec Item 11): a signed-in buyer past the account step has
  // their non-payment progress mirrored to the API so the abandoned-checkout job can
  // send a resume link. Payment fields never leave the browser.
  useCheckoutProgressSync({
    enabled: !!session?.user && openStep !== 'account' && (formIsDirty || hasProgressed),
    phase,
    openStep,
    values: formValues,
    onSave: onProgressSave,
  });

  useEffect(() => {
    if (phase === 'confirmation') {
      clearPersistedCheckoutFields(storageKey);
    }
  }, [phase, storageKey]);

  // Changing the billing plan (spec Item 7) re-collapses every downstream step to
  // incomplete — buyers must re-confirm each one against the new plan. Entered form
  // values are preserved; the account session cannot be invalidated.
  const previousPlanIdRef = useRef(billingPlan.billingPlanId);
  useEffect(() => {
    if (previousPlanIdRef.current === billingPlan.billingPlanId) return;
    previousPlanIdRef.current = billingPlan.billingPlanId;

    const downstream = CHECKOUT_STEP_ORDER.filter(step => step !== 'billingPlan' && step !== 'account');
    const affected = downstream.filter(step => manuallyCompletedSteps.has(step));
    setInvalidatedSteps(new Set(affected));
    setManuallyCompletedSteps(
      new Set([...manuallyCompletedSteps].filter(step => !downstream.includes(step))),
    );
    setKiloClassErrorMessage(null);
  }, [billingPlan.billingPlanId, manuallyCompletedSteps]);

  const purchaseTarget = `${form.values.whoIsThisFor}:${form.values.profileId ?? ''}`;
  const previousPurchaseTargetRef = useRef(purchaseTarget);
  useEffect(() => {
    if (previousPurchaseTargetRef.current === purchaseTarget) return;
    previousPurchaseTargetRef.current = purchaseTarget;

    setManuallyCompletedSteps(previous => {
      if (!PURCHASE_TARGET_STEPS.some(step => previous.has(step))) return previous;
      return new Set([...previous].filter(step => !PURCHASE_TARGET_STEPS.includes(step)));
    });
  }, [purchaseTarget]);

  return (
    <CheckoutContext.Provider
      value={{
        form,
        packageData,
        billingPlan,
        athleteInfoWasPrepopulated,
        setAthleteInfoWasPrepopulated,
        steps,
        openStep,
        setOpenStep,
        isStepCompleted,
        isStepSkipped,
        isStepUnlocked,
        isStepInvalidated,
        isStepEditable,
        completeStep,
        expandStep,
        signOut,
        isSigningOut,
        reopenStepForError,
        allStepsCompleted,
        paymentStepError,
        agreement,
        phase,
        setPhase,
        wasResumedFromStorage: !!restoredSnapshot,
        resumeSource,
        appliedDiscount,
        setAppliedDiscount,
        loggedInAthleteFullName,
        loggedInAthleteEmail,
        checkoutResult,
        setCheckoutResult,
        kiloClassErrorMessage,
        setKiloClassErrorMessage,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = (): CheckoutContextType => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckout must be used within CheckoutProvider');
  }

  return context;
};
